/**
 * Shared layout for ASA's own pages (offers, purchase, confirmation): the village surface, a
 * centred column, and the bracket heading of ASA's venue pages.
 */
import styled from '@emotion/styled';
import { Box, ButtonBase } from '@mui/material';
import { ASA_COLOR } from '../../Utils/asaBrand';
import { DFC_VILLAGE_DESKTOP_MEDIA } from '../../hooks/useDfcVillageDesktop';
import { SurfaceProp, focusRing, omit } from '../../components/shared/dfcVillage/style';

export const AsaScreen = styled(Box, omit('surface'))<SurfaceProp>`
  width: 100%;
  min-height: calc(100vh - 60px);
  background: ${({ surface }) => surface.pageBg};
  color: ${({ surface }) => surface.ink};
  font-family: Inter, system-ui, sans-serif;
`;

export const AsaColumn = styled(Box, omit('wide'))<{ wide?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: ${({ wide }) => (wide ? 1200 : 720)}px;
  margin: 0 auto;
  padding: 24px 16px 48px;

  ${DFC_VILLAGE_DESKTOP_MEDIA} {
    gap: 36px;
    padding: 44px 40px 64px;
  }
`;

export const AsaHeading = styled('h1', omit('surface'))<SurfaceProp>`
  && {
    margin: 0;
    font-size: 36px;
    line-height: 1;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: -0.02em;
    color: ${({ surface }) => surface.ink};

    ${DFC_VILLAGE_DESKTOP_MEDIA} {
      font-size: 60px;
    }
  }
`;

/** Tier and membership cards: three across from the desktop width. */
export const OfferGrid = styled(Box)`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 14px;

  ${DFC_VILLAGE_DESKTOP_MEDIA} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 18px;
  }
`;

export const OfferCard = styled(Box, omit('surface'))<SurfaceProp>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 22px 20px;
  border-radius: 20px;
  background: ${({ surface }) => surface.surface};
  border: 1px solid ${({ surface }) => surface.border};
  border-top: 6px solid ${({ surface }) => surface.accentMark};
`;

export const OfferName = styled('span', omit('surface'))<SurfaceProp>`
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ surface }) => surface.accentText};
`;

export const OfferBig = styled('span', omit('surface'))<SurfaceProp>`
  font-size: 34px;
  line-height: 1.05;
  font-weight: 800;
  color: ${({ surface }) => surface.ink};
`;

export const OfferLine = styled('span', omit('surface'))<SurfaceProp>`
  font-size: 14px;
  color: ${({ surface }) => surface.ink2};
`;

/** "AED 150 extra": royal-blue fill with white text (7.34:1) in both modes. */
export const ExtraChip = styled('span')`
  align-self: flex-start;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  /* Club 15 sets these (club15Brand.ts); unset, ASA's royal blue and white. */
  background: var(--club15-chip-bg, ${ASA_COLOR.royalBlue});
  color: var(--club15-chip-fg, ${ASA_COLOR.white});
`;

export const BuyButton = styled(ButtonBase, omit('surface'))<SurfaceProp>`
  && {
    margin-top: auto;
    min-height: 46px;
    border-radius: 999px;
    font: inherit;
    font-size: 15px;
    font-weight: 800;
    background: ${({ surface }) => surface.ctaBg};
    color: ${({ surface }) => surface.ctaFg};
    ${({ surface }) => focusRing(surface)}
  }
`;

/**
 * Re-themes the generic purchase page parts that have no village variant (the package /
 * membership summary card, its labels, the page wrapper's top margin), inside ASA's shell only.
 */
export const FlowFrame = styled(Box, omit('surface'))<SurfaceProp>`
  width: 100%;

  /* \`&&\` doubles the class, so these win over the purchase pages' own wrappers. */
  && > div {
    width: 100% !important;
    margin-top: 0 !important;
    padding: 0 !important;
  }
  && .package-details-container,
  && .membership-details-container {
    background: ${({ surface }) => surface.surface};
    border: 1px solid ${({ surface }) => surface.border};
    border-top: 6px solid ${({ surface }) => surface.accentMark};
    color: ${({ surface }) => surface.ink};
  }
  && .package-details-container .MuiTypography-root,
  && .membership-details-container .MuiTypography-root,
  && .package-details-label {
    color: ${({ surface }) => surface.ink} !important;
    /* The generic card paints its name and price as gradient-clipped text; use plain ink. */
    -webkit-text-fill-color: currentColor !important;
    background: none !important;
  }
  && .package-details-items {
    background: transparent;
    box-shadow: none;
  }
  && .package-details-container svg,
  && .membership-details-container svg {
    color: ${({ surface }) => surface.accentText};
  }
`;
